import { user } from 'utils/auth/user';
import { IBrand } from 'data/schema';

/**
 * Brand-specific Whatfix script URLs.
 * Add new brand URLs here as they become available.
 */
const scriptUrls: Partial<Record<IBrand, string>> = {
  A: 'https://eucdn.whatfix.com/prod/7ca3a171-5725-4025-a35b-b2dd181c3885/initiator/initiator.nocache.js',
  V: 'https://eucdn.whatfix.com/prod/e64a738d-3550-4573-927d-a9437db3d27b/initiator/initiator.nocache.js',
  N: 'https://eucdn.whatfix.com/prod/f89ab93e-2815-409e-8da4-c102a679f2e0/initiator/initiator.nocache.js',
};

/**
 * Brands that have Whatfix support enabled.
 */
export const whatfixSupportedBrands: IBrand[] = Object.keys(scriptUrls) as IBrand[];

/**
 * Checks if Whatfix is enabled via the WHATFIX_ENABLED environment variable.
 */
export function isWhatfixEnabled(): boolean {
  return process.env.WHATFIX_ENABLED === 'true';
}

/**
 * Checks if a Whatfix script has already been added to the document.
 */
function isWhatfixScriptAlreadyAdded(brand: IBrand): boolean {
  const url = scriptUrls[brand];
  if (!url) return false;
  return !!document.querySelector(`script[src="${url}"]`);
}

/**
 * Adds the Whatfix script to the document head.
 */
function addWhatfixScript(url: string): void {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = url;
  document.head.appendChild(script);
}

let previousBrand: IBrand | undefined;

/**
 * Loads the brand-specific Whatfix script and sets global context variables.
 * If the brand changes after initial load, the page will reload to ensure
 * the correct script is loaded (Whatfix scripts are brand-specific).
 */
function loadWhatfixScript(language: string, market: string, brand: IBrand): void {
  // Only reload script when brand changes and not when just market changes
  if (brand !== previousBrand) {
    previousBrand = brand;

    if (isWhatfixScriptAlreadyAdded(brand)) {
      window.location.reload();
      return;
    }

    const url = scriptUrls[brand];
    if (!url) {
      return;
    }
    addWhatfixScript(url);
  }

  // Set global context variables for Whatfix to consume
  window.WHATFIX_MARKET = market;
  window.WHATFIX_ROLES = user.groups;
  window.WHATFIX_USER_PSEUDONYM = user.userPseudonym;
  window.WHATFIX_LANGUAGE = language;
}

/**
 * Initializes Whatfix with user context.
 * Only initializes for dealer users when Whatfix is enabled.
 *
 * @param language - The user's language/locale (e.g., 'de', 'en_US')
 * @param market - The 3-letter market code (e.g., 'DEU', 'USA')
 * @param brand - The single-letter brand code (e.g., 'A', 'V', 'N')
 */
export async function initWhatfix(language: string, market: string, brand: IBrand): Promise<void> {
  if (!isWhatfixEnabled()) {
    return;
  }

  if (user.type !== 'dealer') {
    return;
  }

  loadWhatfixScript(language, market, brand);
}

/**
 * Opens the Whatfix assistant/help panel.
 */
export function openWhatfix(): void {
  if (typeof window._wfx_selfhelp_open === 'function') {
    window._wfx_selfhelp_open();
  }
}
